/**
 * Positions of the Sun, Moon and planets.
 *
 * ## Fidelity (Tier B)
 *
 * - **Sun**: low-precision analytic series. Accurate to about 0.01 degrees, which
 *   is far better than needed for lighting, the day/night terminator and solar
 *   radiation pressure.
 * - **Moon**: the principal terms of Meeus's lunar theory. Accurate to roughly
 *   0.1 degrees in longitude and a few hundred kilometres in distance — enough for
 *   third-body perturbations and for planning a lunar transfer, but not for
 *   navigating one.
 * - **Planets**: Standish's linear-rate mean elements, valid 1800–2050, giving
 *   arcminute-level accuracy. Enough for porkchop plots and delta-V estimates.
 *
 * Full JPL DE-series ephemerides would be more accurate by orders of magnitude and
 * are the right answer for real navigation. They are also tens of megabytes, which
 * is not a reasonable thing to ship to a browser for a planning tool.
 */

import { AU, EARTH, TAU, type CelestialBodyParams } from './constants.js';
import { eclipticToEquatorial } from './frames.js';
import { solveKeplerElliptic, trueFromEccentric } from './kepler.js';
import { julianCenturies } from './time.js';
import { wrapTwoPi } from './units.js';
import { norm, normalize, scale, type ReadonlyVec3, type Vec3 } from './vec3.js';

const DEG = Math.PI / 180;

/**
 * Geocentric position of the Sun in the equatorial (ECI) frame, m.
 *
 * Low-precision series from the Astronomical Almanac. Drives lighting, the
 * terminator, eclipse geometry and solar radiation pressure.
 */
export function sunPosition(tEpochSeconds: number): Vec3 {
  const T = julianCenturies(tEpochSeconds);

  // Mean longitude and mean anomaly of the Sun, degrees.
  const meanLongitude = 280.46646 + 36000.76983 * T + 0.0003032 * T * T;
  const meanAnomaly = (357.52911 + 35999.05029 * T - 0.0001537 * T * T) * DEG;

  // Equation of the centre.
  const centre =
    (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(meanAnomaly) +
    (0.019993 - 0.000101 * T) * Math.sin(2 * meanAnomaly) +
    0.000289 * Math.sin(3 * meanAnomaly);

  const trueLongitude = (meanLongitude + centre) * DEG;

  // Radius vector in astronomical units.
  const eccentricity = 0.016708634 - 0.000042037 * T - 0.0000001267 * T * T;
  const trueAnomaly = meanAnomaly + centre * DEG;
  const radius =
    ((1.000001018 * (1 - eccentricity * eccentricity)) / (1 + eccentricity * Math.cos(trueAnomaly))) *
    AU;

  // Ecliptic coordinates: the Sun's ecliptic latitude is zero by definition.
  const ecliptic: Vec3 = [
    radius * Math.cos(trueLongitude),
    radius * Math.sin(trueLongitude),
    0,
  ];
  return eclipticToEquatorial(ecliptic);
}

/** Unit vector from the Earth toward the Sun, in ECI. */
export const sunDirection = (tEpochSeconds: number): Vec3 => normalize(sunPosition(tEpochSeconds));

/**
 * Geocentric position of the Moon in the equatorial (ECI) frame, m.
 *
 * Principal terms of Meeus, *Astronomical Algorithms*, chapter 47. The six largest
 * periodic terms in longitude capture the evection, variation and annual equation,
 * which together account for most of the Moon's departure from a simple ellipse.
 */
export function moonPosition(tEpochSeconds: number): Vec3 {
  const T = julianCenturies(tEpochSeconds);

  // Moon's mean longitude, mean elongation, mean anomaly, argument of latitude.
  const L = (218.3164477 + 481267.88123421 * T) * DEG;
  const D = (297.8501921 + 445267.1114034 * T) * DEG;
  const M = (357.5291092 + 35999.0502909 * T) * DEG;
  const Mprime = (134.9633964 + 477198.8675055 * T) * DEG;
  const F = (93.272095 + 483202.0175233 * T) * DEG;

  // Ecliptic longitude, degrees.
  const longitude =
    L / DEG +
    6.289 * Math.sin(Mprime) +
    1.274 * Math.sin(2 * D - Mprime) +
    0.658 * Math.sin(2 * D) +
    0.214 * Math.sin(2 * Mprime) -
    0.186 * Math.sin(M) -
    0.114 * Math.sin(2 * F);

  // Ecliptic latitude, degrees.
  const latitude =
    5.128 * Math.sin(F) +
    0.281 * Math.sin(Mprime + F) -
    0.278 * Math.sin(F - Mprime) -
    0.173 * Math.sin(2 * D - F);

  // Distance, kilometres.
  const distanceKm =
    385_000.56 -
    20_905.355 * Math.cos(Mprime) -
    3699.111 * Math.cos(2 * D - Mprime) -
    2955.968 * Math.cos(2 * D) -
    569.925 * Math.cos(2 * Mprime);

  const lon = longitude * DEG;
  const lat = latitude * DEG;
  const r = distanceKm * 1000;

  const ecliptic: Vec3 = [
    r * Math.cos(lat) * Math.cos(lon),
    r * Math.cos(lat) * Math.sin(lon),
    r * Math.sin(lat),
  ];
  return eclipticToEquatorial(ecliptic);
}

/** Geocentric lunar velocity in ECI, m/s, by centred differentiation. */
export function moonVelocity(tEpochSeconds: number, dt = 10): Vec3 {
  const before = moonPosition(tEpochSeconds - dt);
  const after = moonPosition(tEpochSeconds + dt);
  return [
    (after[0] - before[0]) / (2 * dt),
    (after[1] - before[1]) / (2 * dt),
    (after[2] - before[2]) / (2 * dt),
  ];
}

/** Unit vector from the Earth toward the Moon, in ECI. */
export const moonDirection = (tEpochSeconds: number): Vec3 => normalize(moonPosition(tEpochSeconds));

/** Phase of the Moon, 0 at new and 1 at full. */
export function moonPhase(tEpochSeconds: number): number {
  const sun = sunDirection(tEpochSeconds);
  const moon = moonDirection(tEpochSeconds);
  const elongation = Math.acos(Math.max(-1, Math.min(1, sun[0] * moon[0] + sun[1] * moon[1] + sun[2] * moon[2])));
  return elongation / Math.PI;
}

// --- Planets -----------------------------------------------------------------

/**
 * Heliocentric mean orbital elements with linear rates (Standish, JPL).
 * Angles in degrees, rates per Julian century. Semi-major axis in AU.
 */
interface PlanetElements {
  readonly a: readonly [number, number];
  readonly e: readonly [number, number];
  readonly i: readonly [number, number];
  /** Mean longitude. */
  readonly L: readonly [number, number];
  /** Longitude of perihelion. */
  readonly varpi: readonly [number, number];
  /** Longitude of the ascending node. */
  readonly Omega: readonly [number, number];
}

const PLANET_ELEMENTS: Record<string, PlanetElements> = {
  mercury: {
    a: [0.38709927, 0.00000037],
    e: [0.20563593, 0.00001906],
    i: [7.00497902, -0.00594749],
    L: [252.2503235, 149472.67411175],
    varpi: [77.45779628, 0.16047689],
    Omega: [48.33076593, -0.12534081],
  },
  venus: {
    a: [0.72333566, 0.0000039],
    e: [0.00677672, -0.00004107],
    i: [3.39467605, -0.0007889],
    L: [181.9790995, 58517.81538729],
    varpi: [131.60246718, 0.00268329],
    Omega: [76.67984255, -0.27769418],
  },
  earth: {
    a: [1.00000261, 0.00000562],
    e: [0.01671123, -0.00004392],
    i: [-0.00001531, -0.01294668],
    L: [100.46457166, 35999.37244981],
    varpi: [102.93768193, 0.32327364],
    Omega: [0, 0],
  },
  mars: {
    a: [1.52371034, 0.0000018],
    e: [0.0933941, 0.00007882],
    i: [1.84969142, -0.00813131],
    L: [-4.55343205, 19140.30268499],
    varpi: [-23.94362959, 0.44441088],
    Omega: [49.55953891, -0.29257343],
  },
  jupiter: {
    a: [5.202887, -0.00011607],
    e: [0.04838624, -0.00013253],
    i: [1.30439695, -0.00183714],
    L: [34.39644051, 3034.74612775],
    varpi: [14.72847983, 0.21252668],
    Omega: [100.47390909, 0.20469106],
  },
  saturn: {
    a: [9.53667594, -0.0012506],
    e: [0.05386179, -0.00050991],
    i: [2.48599187, 0.00193609],
    L: [49.95424423, 1222.49362201],
    varpi: [92.59887831, -0.41897216],
    Omega: [113.66242448, -0.28867794],
  },
};

export type PlanetName = keyof typeof PLANET_ELEMENTS;

/**
 * Heliocentric position of a planet in the equatorial frame of J2000, m.
 *
 * Solves the planet's Kepler problem from the linear-rate mean elements, then
 * rotates from the perifocal frame through the ecliptic to the equator.
 */
export function planetPosition(planet: PlanetName, tEpochSeconds: number): Vec3 {
  const elements = PLANET_ELEMENTS[planet];
  if (!elements) throw new Error(`planetPosition: unknown planet ${planet}`);

  const T = julianCenturies(tEpochSeconds);
  const a = (elements.a[0] + elements.a[1] * T) * AU;
  const e = elements.e[0] + elements.e[1] * T;
  const i = (elements.i[0] + elements.i[1] * T) * DEG;
  const L = (elements.L[0] + elements.L[1] * T) * DEG;
  const varpi = (elements.varpi[0] + elements.varpi[1] * T) * DEG;
  const Omega = (elements.Omega[0] + elements.Omega[1] * T) * DEG;

  const argumentOfPerihelion = varpi - Omega;
  const meanAnomaly = wrapTwoPi(L - varpi);

  const eccentricAnomaly = solveKeplerElliptic(meanAnomaly, e);
  const trueAnomaly = trueFromEccentric(eccentricAnomaly, e);
  const radius = a * (1 - e * Math.cos(eccentricAnomaly));

  // Perifocal coordinates.
  const xPerifocal = radius * Math.cos(trueAnomaly);
  const yPerifocal = radius * Math.sin(trueAnomaly);

  const cosArg = Math.cos(argumentOfPerihelion);
  const sinArg = Math.sin(argumentOfPerihelion);
  const cosNode = Math.cos(Omega);
  const sinNode = Math.sin(Omega);
  const cosInc = Math.cos(i);
  const sinInc = Math.sin(i);

  const ecliptic: Vec3 = [
    (cosArg * cosNode - sinArg * sinNode * cosInc) * xPerifocal +
      (-sinArg * cosNode - cosArg * sinNode * cosInc) * yPerifocal,
    (cosArg * sinNode + sinArg * cosNode * cosInc) * xPerifocal +
      (-sinArg * sinNode + cosArg * cosNode * cosInc) * yPerifocal,
    sinArg * sinInc * xPerifocal + cosArg * sinInc * yPerifocal,
  ];

  return eclipticToEquatorial(ecliptic);
}

/** Heliocentric velocity of a planet, m/s, by central difference. */
export function planetVelocity(planet: PlanetName, tEpochSeconds: number, dt = 60): Vec3 {
  const before = planetPosition(planet, tEpochSeconds - dt);
  const after = planetPosition(planet, tEpochSeconds + dt);
  return [
    (after[0] - before[0]) / (2 * dt),
    (after[1] - before[1]) / (2 * dt),
    (after[2] - before[2]) / (2 * dt),
  ];
}

/** Orbital period of a planet about the Sun, s. */
export function planetPeriod(planet: PlanetName): number {
  const elements = PLANET_ELEMENTS[planet];
  if (!elements) throw new Error(`planetPeriod: unknown planet ${planet}`);
  const a = elements.a[0] * AU;
  const muSun = 1.32712440018e20;
  return TAU * Math.sqrt((a * a * a) / muSun);
}

/** Mean heliocentric orbital radius of a planet, m. */
export const planetSemiMajorAxis = (planet: PlanetName): number => {
  const elements = PLANET_ELEMENTS[planet];
  if (!elements) throw new Error(`planetSemiMajorAxis: unknown planet ${planet}`);
  return elements.a[0] * AU;
};

export const PLANET_NAMES = Object.keys(PLANET_ELEMENTS) as PlanetName[];

// --- Eclipse and illumination ------------------------------------------------

/**
 * Whether a spacecraft is in the Earth's shadow, and how deeply.
 *
 * Returns 1 in full sunlight, 0 in full umbra, and an intermediate value in the
 * penumbra using a conical shadow model. Eclipse fraction drives solar power,
 * thermal cycling, and whether solar radiation pressure applies at all.
 */
export function illuminationFraction(
  position: ReadonlyVec3,
  tEpochSeconds: number,
  body: CelestialBodyParams = EARTH,
): number {
  const sun = sunPosition(tEpochSeconds);
  const sunDistance = norm(sun);
  const toSun = scale(sun, 1 / sunDistance);
  const r = norm(position);
  if (r === 0) return 0;

  // Component of the position along the anti-sun direction.
  const alongShadow = -(position[0] * toSun[0] + position[1] * toSun[1] + position[2] * toSun[2]);
  if (alongShadow <= 0) return 1; // Sunward side: always lit.

  // Perpendicular distance from the shadow axis.
  const perpendicularSquared = r * r - alongShadow * alongShadow;
  const perpendicular = Math.sqrt(Math.max(0, perpendicularSquared));

  const solarRadius = 6.957e8;
  // Umbra and penumbra cone half-angles.
  const umbraAngle = Math.asin((solarRadius - body.equatorialRadius) / sunDistance);
  const penumbraAngle = Math.asin((solarRadius + body.equatorialRadius) / sunDistance);

  const umbraRadius = body.equatorialRadius - alongShadow * Math.tan(umbraAngle);
  const penumbraRadius = body.equatorialRadius + alongShadow * Math.tan(penumbraAngle);

  if (perpendicular <= umbraRadius) return 0;
  if (perpendicular >= penumbraRadius) return 1;
  // Linear ramp across the penumbra: a full lune-area calculation is not worth it
  // for a quantity that is only used for power and thermal display.
  return (perpendicular - umbraRadius) / (penumbraRadius - umbraRadius);
}

/** Whether the spacecraft is fully in shadow. */
export const inEclipse = (
  position: ReadonlyVec3,
  tEpochSeconds: number,
  body: CelestialBodyParams = EARTH,
): boolean => illuminationFraction(position, tEpochSeconds, body) < 0.01;

/**
 * Subsolar point: the geodetic longitude and latitude directly beneath the Sun.
 * The centre of the illuminated hemisphere, and what the day/night terminator is
 * drawn around.
 */
export function subsolarPoint(
  tEpochSeconds: number,
  gmstRad: number,
): { latitude: number; longitude: number } {
  const sun = sunPosition(tEpochSeconds);
  const r = norm(sun);
  const declination = Math.asin(sun[2] / r);
  const rightAscension = Math.atan2(sun[1], sun[0]);
  let longitude = rightAscension - gmstRad;
  while (longitude > Math.PI) longitude -= TAU;
  while (longitude < -Math.PI) longitude += TAU;
  return { latitude: declination, longitude };
}
